import React, { useState, useEffect } from 'react';
import { BarChart3, Plus, X, Loader2, AlertCircle, Trophy, Swords, Crown, Star, Shield, Users, Skull } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = 'https://chambatron.online:8105';
const API = `${BACKEND_URL}/api`;

const ComparePage = () => {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSelector, setShowSelector] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/profiles`);
      setProfiles(response.data);
    } catch (err) {
      console.error('Error fetching profiles:', err);
      setError('Error al cargar los perfiles');
    } finally {
      setLoading(false);
    }
  };

  const addProfile = (profile) => {
    if (selectedProfiles.length >= 4) return;
    if (selectedProfiles.find(p => p.id === profile.id)) return;
    setSelectedProfiles([...selectedProfiles, profile]);
    setShowSelector(false);
  };

  const removeProfile = (id) => {
    setSelectedProfiles(selectedProfiles.filter(p => p.id !== id));
  };

  const parseNumber = (str) => {
    if (!str) return 0;
    const num = parseFloat(str.replace(/[^0-9.]/g, ''));
    if (str.includes('M')) return num * 1000000;
    if (str.includes('K')) return num * 1000;
    return num;
  };

  const getMaxValue = (key) => {
    if (selectedProfiles.length === 0) return 0;
    return Math.max(...selectedProfiles.map(p => {
      if (key === 'power' || key === 'troopPower') {
        return parseNumber(p[key]);
      }
      return p[key] || 0;
    }));
  };

  const getBarWidth = (profile, key) => {
    const max = getMaxValue(key);
    if (max === 0) return 0;
    let value;
    if (key === 'power' || key === 'troopPower') {
      value = parseNumber(profile[key]);
    } else {
      value = profile[key] || 0;
    }
    return (value / max) * 100;
  };

  const statConfig = [
    { key: 'power', label: 'Poder Total', icon: Swords, color: 'from-yellow-500 to-orange-500' },
    { key: 'troopPower', label: 'Muertes', icon: Skull, color: 'from-pink-500 to-rose-500' },
    { key: 'commanderLevel', label: 'LV Profesión', icon: Crown, color: 'from-green-500 to-emerald-500' },
    { key: 'vipLevel', label: 'Nivel VIP', icon: Star, color: 'from-purple-500 to-violet-500' },
    { key: 'playerLevel', label: 'Nivel Jugador', icon: Shield, color: 'from-cyan-500 to-blue-500' },
  ];

  const profileColors = [
    'from-orange-500 to-yellow-500',
    'from-cyan-500 to-blue-500',
    'from-green-500 to-emerald-500',
    'from-purple-500 to-pink-500'
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-5xl font-black text-white mb-3" style={{textShadow: '0 0 20px rgba(255,255,255,0.2)'}}>
          Comparar <span className="text-cyan-400">Perfiles</span>
        </h1>
        <p className="text-cyan-300/70">
          Selecciona hasta 4 perfiles para comparar sus estadísticas
        </p>
      </div>

      {/* Selected Profiles - Game Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {selectedProfiles.map((profile, index) => {
          const cleanName = profile.playerName?.replace(/\[.*?\]/g, '').trim() || '';
          return (
          <div 
            key={profile.id}
            className={`game-card relative p-4 border-2`}
            style={{borderColor: index === 0 ? '#f97316' : index === 1 ? '#06b6d4' : index === 2 ? '#22c55e' : '#a855f7'}}
          >
            <button
              onClick={() => removeProfile(profile.id)}
              className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 hover:bg-red-400 rounded-full flex items-center justify-center z-10 border-2 border-red-300"
            >
              <X className="w-4 h-4 text-white" />
            </button>
            <div className="text-center">
              {profile.profileImage ? (
                <img 
                  src={profile.profileImage} 
                  alt={cleanName}
                  className="w-16 h-16 mx-auto rounded-lg object-cover border-2 border-white/30 mb-3"
                />
              ) : (
                <div className="w-16 h-16 mx-auto rounded-lg bg-gradient-to-br from-orange-500/30 to-yellow-500/30 flex items-center justify-center mb-3 border border-orange-500/50">
                  <Shield className="w-8 h-8 text-orange-400" />
                </div>
              )}
              <p className="text-white font-bold text-sm truncate">{cleanName}</p>
              <p className="text-cyan-300/70 text-xs">Nvl. {profile.playerLevel}</p>
            </div>
          </div>
        )})}
        
        {selectedProfiles.length < 4 && (
          <div 
            onClick={() => setShowSelector(true)}
            className="game-card border-2 border-dashed border-cyan-500/30 hover:border-cyan-400/50 p-4 flex flex-col items-center justify-center cursor-pointer transition-all duration-300 hover:bg-cyan-500/5 min-h-[140px]"
          >
            <Plus className="w-10 h-10 text-cyan-500/50 mb-2" />
            <p className="text-cyan-500/70 text-sm font-bold">Añadir Perfil</p>
          </div>
        )}
      </div>

      {/* Profile Selector Modal */}
      {showSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setShowSelector(false)}>
          <div className="game-card p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">Seleccionar Perfil</h3>
              <button onClick={() => setShowSelector(false)} className="text-cyan-400 hover:text-white p-1">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
              </div>
            ) : profiles.length === 0 ? (
              <p className="text-cyan-300/70 text-center py-10">No hay perfiles disponibles</p>
            ) : (
              <div className="space-y-2">
                {profiles
                  .filter(p => !selectedProfiles.find(sp => sp.id === p.id))
                  .map(profile => (
                    <button
                      key={profile.id}
                      onClick={() => addProfile(profile)}
                      className="w-full p-4 bg-[#0a1628] hover:bg-cyan-900/30 rounded-xl flex items-center gap-4 transition-colors border border-cyan-500/20 hover:border-cyan-500/50"
                    >
                      {profile.profileImage ? (
                        <img src={profile.profileImage} alt="" className="w-12 h-12 rounded-lg object-cover border border-cyan-500/30" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center border border-orange-500/30">
                          <Shield className="w-6 h-6 text-orange-400" />
                        </div>
                      )}
                      <div className="text-left">
                        <p className="text-white font-bold">{profile.playerName?.replace(/\[.*?\]/g, '').trim()}</p>
                        <p className="text-cyan-300/70 text-xs">{profile.power} • {profile.allianceName}</p>
                      </div>
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comparison Charts */}
      {selectedProfiles.length >= 2 && (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-cyan-400" />
            Comparación de Estadísticas
          </h2>
          
          {statConfig.map(stat => {
            const Icon = stat.icon;
            return (
              <div key={stat.key} className="game-card p-6">
                <div className="flex items-center gap-4 mb-5">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center border-2 border-white/30 shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{stat.label}</h3>
                </div>
                
                <div className="space-y-4">
                  {selectedProfiles.map((profile, index) => {
                    const cleanName = profile.playerName?.replace(/\[.*?\]/g, '').trim() || '';
                    return (
                    <div key={profile.id} className="flex items-center gap-4">
                      <div className="w-28 truncate">
                        <span className="text-sm text-cyan-300 font-semibold">{cleanName}</span>
                      </div>
                      <div className="flex-1 h-10 bg-[#0a1628] rounded-xl overflow-hidden relative border border-cyan-500/20">
                        <div 
                          className={`h-full bg-gradient-to-r ${profileColors[index]} transition-all duration-700 rounded-xl`}
                          style={{ width: `${getBarWidth(profile, stat.key)}%` }}
                        ></div>
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white text-sm font-bold" style={{textShadow: '0 0 10px rgba(0,0,0,0.8)'}}>
                          {stat.key === 'power' || stat.key === 'troopPower' ? profile[stat.key] : profile[stat.key]}
                        </span>
                      </div>
                    </div>
                  )})})
                </div>
              </div>
            );
          })}

          {/* Winner Summary */}
          <div className="game-card game-card-gold p-6">
            <div className="flex items-center gap-4 mb-6">
              <Trophy className="w-10 h-10 text-yellow-400" />
              <h3 className="text-2xl font-bold text-white">Resumen de Ganadores</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {statConfig.map(stat => {
                const max = getMaxValue(stat.key);
                const winner = selectedProfiles.find(p => {
                  const val = stat.key === 'power' || stat.key === 'troopPower' 
                    ? parseNumber(p[stat.key]) 
                    : p[stat.key];
                  return val === max;
                });
                const winnerName = winner?.playerName?.replace(/\[.*?\]/g, '').trim() || '';
                return (
                  <div key={stat.key} className="flex items-center justify-between bg-[#0a1628]/50 rounded-xl p-4 border border-yellow-500/20">
                    <span className="text-cyan-300 font-semibold">{stat.label}:</span>
                    <span className="text-yellow-400 font-bold text-lg">{winnerName}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {selectedProfiles.length < 2 && (
        <div className="game-card p-12 text-center">
          <BarChart3 className="w-16 h-16 mx-auto mb-4 text-cyan-600/50" />
          <h3 className="text-2xl font-bold text-white mb-2">Selecciona al menos 2 perfiles</h3>
          <p className="text-cyan-300/70">Añade perfiles para ver la comparación de estadísticas</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="game-card border-red-500 p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <span className="text-cyan-100">{error}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparePage;
